import { ColumnDef, createColumnHelper, Row } from '@tanstack/react-table';
import { DataTable } from 'components/table/DataTable';
import { useModelService } from 'services/model/model.service';
import { useIntl } from 'react-intl';
import { DataTableContextProvider } from 'components/table/context/DataTableContextProvider';
import { useEntriesDemandReportData } from 'pages/insights-pro/performance-tab/reports/demand-monitoring/queryHooks';
import { useContext, useEffect, useState } from 'react';
import { TenantConfigContext } from 'context/TenantConfigContext';
import {
  buildStandardColumns,
  createCellExportOptions,
  DemandTableProps,
  DemandTableRow,
  digestData,
} from 'pages/insights-pro/performance-tab/reports/demand-monitoring/DemandTableHelpers';
import { ProductDetailsTableRow } from 'pages/insights-pro/performance-tab/reports/demand-monitoring/tables/ProductDetailsTableRow';
import { DEMAND_MONITORING_ROOT } from 'pages/insights-pro/performance-tab/reports/demand-monitoring/DemandHome';
import { useNavigate } from 'react-router-dom';

interface DemandMarketplaceTableRow extends DemandTableRow {
  chnMarketplace: string;
}

export function MarketplaceTable({ merchantData, filters }: DemandTableProps) {
  const intl = useIntl();
  const navigate = useNavigate();

  const { getMeasureByMetric, getAttributes } = useModelService();
  const [chnMarketplace] = getAttributes(['chnMarketplace']);
  const ordGmvVal = getMeasureByMetric('ordGmvVal', 'OD');
  const ordQty = getMeasureByMetric('ordQty', 'OD');

  const columnHelper = createColumnHelper<DemandMarketplaceTableRow>();

  const defaultColumns = buildStandardColumns(ordQty, intl);

  const buildColumns = () => [
    columnHelper.accessor('chnMarketplace', {
      id: 'chnMarketplace',
      header: () => chnMarketplace.name,
      cell: info => info.getValue(),
    }),
  ];

  const cols: ColumnDef<DemandMarketplaceTableRow>[] = (buildColumns() as ColumnDef<ProductDetailsTableRow>[]).concat(
    defaultColumns as ColumnDef<ProductDetailsTableRow>[]
  ) as ColumnDef<DemandMarketplaceTableRow>[];

  const { data } = useEntriesDemandReportData(chnMarketplace.code, filters);

  const { currentDataVersion } = useContext(TenantConfigContext);

  const [tableRows, setTableRows] = useState<DemandMarketplaceTableRow[]>([]);

  useEffect(() => {
    if (currentDataVersion) {
      digestData(
        merchantData,
        data,
        chnMarketplace,
        ordGmvVal,
        ordQty,
        entry => ({
          chnMarketplace: chnMarketplace.getValue(entry.attributes),
        }),
        setTableRows
      );
    }
  }, [data, merchantData, currentDataVersion]);

  const showDetails = (row: Row<DemandMarketplaceTableRow>) => {
    navigate(
      `${DEMAND_MONITORING_ROOT}/detail/${chnMarketplace.code}/${row.original.chnMarketplace}${location.search}`
    );
  };

  return (
    <>
      <DataTableContextProvider>
        <DataTable
          onRowClick={showDetails}
          rows={tableRows}
          columns={cols}
          titleId="insights.marketplace"
          defaultSortingState={[{ id: 'ordGmvVal', desc: true }]}
          exportConfigOptions={createCellExportOptions('Marketplaces')}
        />
      </DataTableContextProvider>
    </>
  );
}
